import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ActivityIndicator,
	Image,
	Keyboard,
	StyleSheet,
	Text,
	TextInput,
	View,
} from 'react-native';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import ProfilePlaceholder from '../../../assets/header/player-icon.png';
import { IconCloseSvg } from '../../../assets/inventory';
import CalendarIcon from '../../../assets/profileRedesign/calendar.svg';
import EditIcon from '../../../assets/profileRedesign/edit.svg';
import LocationIcon from '../../../assets/profileRedesign/location.svg';
import PictureIcon from '../../../assets/profileRedesign/picture-icon.png';
import ProfileIcon from '../../../assets/profileRedesign/profile.png';
import VipCrown from '../../../assets/profileRedesign/vip-crown.svg';
import { goldGlow } from '../../../shared/glowStyles';
import { RootState } from '../../../store';
import { profileRequest, updateProfileRequest } from '../../../store/auth/authActions';
import { selectProfile } from '../../../store/auth/authReducer';
import type { UpdateProfilePayload } from '../../../store/types';
import { getAvatarUrl, uploadFile } from '../../../utils/axiosInstance';
import { getAvatarUploadErrorMessage } from '../../../utils/helpers/avatarUpload';
import { toast } from '../../../utils/helpers/toastUtils';
import { useDevice } from '../../../utils/hooks/useDevice';
import { ACHIEVEMENTS_FONT } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import { DESKTOP_TWIN_FACTOR, useRedesignScale } from '../Modals/useRedesignScale';
import { pickAvatarFile } from './pickAvatarFile';
import ProfileRedesignEditDesktop, {
	DESKTOP_PANEL_DESIGN_HEIGHT,
	EditFieldKey,
	EditFieldState,
} from './ProfileRedesignEditDesktop';
import { BuyUsernameChangerButton, LockGlyph } from './UsernameLock';

type Props = {
	onBack: () => void;
	onClose: () => void;
	onBuyUsernameChanger: () => void;
	renderInline?: boolean;
};

const PANEL = { height: 641, top: 69 };

/**
 * The panel this modal is drawn in. The profile modal takes the same one, so moving between the
 * profile and its edit view never changes the panel's size: on a phone 641 high at 69, on desktop
 * the "Edit Profile / Desktop" twin's panel at that artboard's scale.
 */
export const EDIT_PROFILE_PANEL = PANEL;
/**
 * The scrim behind the profile panel - a step darker than the shared 0.45 - and behind the
 * inventory and friends tabs that share that panel, so switching tabs never changes it.
 */
export const PROFILE_SCRIM = 'rgba(11,2,1,0.75)';
/**
 * The profile panel's geometry. The inventory modal draws its Inventory and Friends tabs in the
 * same panel, so moving between the three tabs never resizes or moves it.
 */
export const useProfilePanel = () => {
	const { isDesktop } = useDevice();
	return isDesktop
		? { height: DESKTOP_PANEL_DESIGN_HEIGHT, scrimColor: PROFILE_SCRIM }
		: { height: PANEL.height, top: PANEL.top, scrimColor: PROFILE_SCRIM };
};
export const useEditProfileScale = (
	desktopHeight: number = DESKTOP_PANEL_DESIGN_HEIGHT,
	mobileExtraHeight = 0,
) => {
	const { isDesktop } = useDevice();
	return useRedesignScale(
		isDesktop
			? { fitHeight: desktopHeight, desktopFactor: DESKTOP_TWIN_FACTOR }
			: { fitHeight: PANEL.top + PANEL.height + mobileExtraHeight + 16 },
	);
};
/** Product cap; the players service's MaxLength on description is still 100. */
const DESCRIPTION_LIMIT = 50;
const DESCRIPTION_HEIGHT = 48;
/** Server messages that mean the requested username belongs to someone else. */
const USERNAME_TAKEN = /taken|already|exists|in use|duplicate/i;
/** The Locked username field carries the "Buy username changer" CTA: 147 tall instead of 107. */
const LOCKED_FIELD_EXTRA = 40;
/** How far a desktop content re-measure has to move before the panel is resized to it. */
const DESKTOP_HEIGHT_TOLERANCE = 2;

const ProfileRedesignEditView = ({
	onBack,
	onClose,
	onBuyUsernameChanger,
	renderInline = false,
}: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const user = useSelector(selectProfile);
	const { profileLoading, profileError } = useSelector((state: RootState) => state.auth);
	const { isDesktop } = useDevice();
	const [measuredDesktopHeight, setDesktopHeight] = useState(DESKTOP_PANEL_DESIGN_HEIGHT);
	// Never shorter than the artboard's panel - the profile modal is drawn at exactly that height -
	// but free to grow if the content needs more (an error line under a field, say).
	const desktopHeight = Math.max(measuredDesktopHeight, DESKTOP_PANEL_DESIGN_HEIGHT);
	// The content is measured at the scale this height produces, and not everything in it scales
	// linearly, so each new scale measures a hair differently. Taking every reading made the panel
	// chase itself forever - visible as a jitter once the content outgrew the artboard (a locked
	// username). Readings within a couple of design units of the current one are the same height.
	const updateDesktopHeight = useCallback(
		(height: number) =>
			setDesktopHeight(current =>
				Math.abs(height - current) < DESKTOP_HEIGHT_TOLERANCE ? current : height,
			),
		[],
	);
	const usernameChangesLeft = user?.usernameChangesLeft;
	// VIP renames are unlimited (null); the field locks once a non-VIP player's free ones are spent.
	const isUsernameLocked = !user?.isVip && usernameChangesLeft === 0;
	const usernameLockedHelper = t('T_SITE_PROFILE_USERNAME_LOCKED', {
		defaultValue: 'Username can’t be changed.',
	});
	const usernameRestHelper =
		usernameChangesLeft != null
			? `${t('T_SITE_PROFILE_MODAL_USERNAME_CHANGES', { defaultValue: 'Username changes left' })}: ${usernameChangesLeft}`
			: t('T_SITE_PROFILE_USERNAME_LENGTH_RULE', { defaultValue: '3–16 characters.' });
	const avatarChangesLeft = user?.avatarChangesLeft;
	// Same quota as the username: unlimited (null) for VIP, the camera goes inert once it is spent.
	const isAvatarLocked = !user?.isVip && avatarChangesLeft === 0;
	const avatarChangesNote = user?.isVip
		? t('T_SITE_PROFILE_AVATAR_CHANGES_UNLIMITED', {
				defaultValue: 'Remaining avatar changes: Unlimited',
			})
		: avatarChangesLeft != null
			? t('T_SITE_PROFILE_AVATAR_CHANGES_LEFT', {
					defaultValue: 'Remaining avatar changes: {{count}}',
					count: avatarChangesLeft,
				})
			: undefined;
	const lockedExtra = isUsernameLocked ? LOCKED_FIELD_EXTRA : 0;
	const scale = useEditProfileScale(desktopHeight, lockedExtra);
	const startedSaving = useRef(false);
	const saveRef = useRef<View>(null);
	const [username, setUsername] = useState('');
	const [age, setAge] = useState('');
	const [location, setLocation] = useState('');
	const [description, setDescription] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | undefined>();
	const [usernameError, setUsernameError] = useState<string | undefined>();
	const [focusedField, setFocusedField] = useState<EditFieldKey | undefined>();
	// A new photo is uploaded as soon as it is picked, but only becomes the avatar on SAVE.
	const [pendingAvatar, setPendingAvatar] = useState<{ fileId: string; previewUri: string }>();
	const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
	const isUsernameEdited = username !== (user?.username ?? '');
	const isUsernameTooShort = isUsernameEdited && username.trim().length < 3;
	const savedAge = user?.age != null ? String(user.age) : '';
	const savedLocation = user?.location ?? '';
	const savedDescription = user?.description ?? '';
	const isDirty =
		isUsernameEdited ||
		age !== savedAge ||
		location !== savedLocation ||
		description !== savedDescription ||
		!!pendingAvatar;
	const styles = createStyles(scale, lockedExtra);
	const fieldLabel = (
		type: 'user' | 'age' | 'location' | 'description',
		children: string,
		muted = false,
	) => (
		<View style={styles.labelRow}>
			{type === 'user' ? <Image source={ProfileIcon} style={styles.labelIcon} /> : null}
			{type === 'age' ? (
				<CalendarIcon width={14 * scale} height={14 * scale} style={styles.vectorLabelIcon} />
			) : null}
			{type === 'location' ? (
				<LocationIcon width={14 * scale} height={14 * scale} style={styles.vectorLabelIcon} />
			) : null}
			{type === 'description' ? (
				<EditIcon width={14 * scale} height={14 * scale} style={styles.vectorLabelIcon} />
			) : null}
			<Text style={[styles.label, muted && styles.mutedLabel]}>{children}</Text>
		</View>
	);
	// Buying another rename isn't built yet, so the CTA does nothing for now.
	const buyUsernameChanger = onBuyUsernameChanger;

	useEffect(() => {
		if (!user) dispatch(profileRequest());
	}, [dispatch, user]);

	useEffect(() => {
		if (!user || isSubmitting) return;
		setUsername(user.username ?? '');
		setAge(user.age != null ? String(user.age) : '');
		setLocation(user.location ?? '');
		setDescription(user.description ?? '');
	}, [isSubmitting, user]);

	useEffect(() => {
		if (!isSubmitting) return;
		if (profileLoading) {
			startedSaving.current = true;
			return;
		}
		if (!startedSaving.current) return;

		setIsSubmitting(false);
		startedSaving.current = false;
		if (profileError) {
			// Desktop draws a taken username on the field itself ("Error — Username taken").
			if (isDesktop && USERNAME_TAKEN.test(profileError))
				setUsernameError(
					t('T_SITE_PROFILE_USERNAME_TAKEN', { defaultValue: 'That username is taken.' }),
				);
			else setSubmitError(profileError);
			return;
		}
		setPendingAvatar(undefined);
		toast.success(
			'profile.update_success_title',
			t('T_SITE_PROFILE_UPDATED', { defaultValue: 'Profile updated' }),
			t('T_SITE_PROFILE_CHANGES_SAVED', { defaultValue: 'Your changes have been saved' }),
		);
		onBack();
	}, [isDesktop, isSubmitting, onBack, profileError, profileLoading, t]);

	const isSaving = isSubmitting || profileLoading || isUploadingAvatar;
	const isSaveInert = !isDirty || isUsernameTooShort || !!usernameError;
	const avatarSource = pendingAvatar
		? { uri: pendingAvatar.previewUri }
		: user?.avatarFileId
			? { uri: getAvatarUrl(user.avatarFileId) }
			: ProfilePlaceholder;
	const changePhoto = async () => {
		if (isSaving || isAvatarLocked) return;
		const picked = await pickAvatarFile();
		if (!picked) return;
		setIsUploadingAvatar(true);
		try {
			const fileId = await uploadFile(picked.file);
			setPendingAvatar({ fileId, previewUri: picked.previewUri });
		} catch (err) {
			toast.errorText(
				t('T_SITE_AVATAR_UPLOAD_FAILED', { defaultValue: 'Avatar upload failed' }),
				getAvatarUploadErrorMessage(err),
			);
		} finally {
			setIsUploadingAvatar(false);
		}
	};
	const save = () => {
		const trimmedUsername = username.trim();
		if (isUsernameEdited && !isUsernameLocked) {
			if (!trimmedUsername) {
				setSubmitError(
					t('T_SITE_PROFILE_MODAL_ERROR_USERNAME_REQUIRED', {
						defaultValue: 'Username is required',
					}),
				);
				return;
			}
			if (trimmedUsername.length < 3) {
				setSubmitError(
					t('T_SITE_PROFILE_USERNAME_MIN_LENGTH', {
						defaultValue: 'Username must be at least 3 characters',
					}),
				);
				return;
			}
		}

		// Only what changed goes to the server. The players service counts every request that
		// carries a username as a username change - non-VIP players get two - so sending the
		// unchanged name along with an age or location edit used one up, and after two saves of
		// anything the profile could no longer be saved at all.
		const payload: UpdateProfilePayload = {};
		if (!isUsernameLocked && isUsernameEdited && trimmedUsername !== (user?.username ?? '')) {
			payload.username = trimmedUsername;
		}
		if (age !== savedAge) payload.age = age ? Number(age) : null;
		if (location !== savedLocation) payload.location = location.trim() || null;
		if (description !== savedDescription) payload.description = description.trim() || null;
		if (pendingAvatar) payload.avatarFileId = pendingAvatar.fileId;
		if (!Object.keys(payload).length) {
			onBack();
			return;
		}

		setSubmitError(undefined);
		setUsernameError(undefined);
		startedSaving.current = false;
		setIsSubmitting(true);
		dispatch(updateProfileRequest(payload));
	};

	if (isDesktop) {
		const savedValues: Record<EditFieldKey, string> = {
			username: user?.username ?? '',
			age: user?.age != null ? String(user.age) : '',
			location: user?.location ?? '',
			description: savedDescription,
		};
		const values: Record<EditFieldKey, string> = { username, age, location, description };
		const fieldState = (key: EditFieldKey): EditFieldState => {
			if (key === 'username' && isUsernameLocked) return 'locked';
			if (key === 'username' && (usernameError || isUsernameTooShort)) return 'error';
			if (focusedField === key) return 'focused';
			if (values[key] !== savedValues[key]) return 'unsaved';
			if (!values[key]) return 'empty';
			return 'default';
		};
		const setters: Record<EditFieldKey, (value: string) => void> = {
			username: value => {
				setUsername(value);
				setUsernameError(undefined);
			},
			age: value => setAge(value.replace(/[^0-9]/g, '').slice(0, 2)),
			location: value => setLocation(value.slice(0, 30)),
			description: value => setDescription(value.slice(0, DESCRIPTION_LIMIT)),
		};

		return (
			<RedesignModal
				scale={scale}
				onClose={onClose}
				renderInline={renderInline}
				panel={{ height: desktopHeight, scrimColor: PROFILE_SCRIM }}
				desktopTwin
			>
				<ProfileRedesignEditDesktop
					scale={scale}
					avatarSource={avatarSource}
					isVip={!!user?.isVip}
					values={values}
					fieldStates={{
						username: fieldState('username'),
						age: fieldState('age'),
						location: fieldState('location'),
						description: fieldState('description'),
					}}
					onChangeField={(key, value) => setters[key](value)}
					onFocusField={setFocusedField}
					onBlurField={key => setFocusedField(current => (current === key ? undefined : current))}
					usernameHelper={
						isUsernameLocked
							? { text: usernameLockedHelper, danger: false }
							: usernameError
								? { text: usernameError, danger: true }
								: isUsernameTooShort
									? {
											text: t('T_SITE_PROFILE_USERNAME_TOO_SHORT', {
												defaultValue: 'Username must be at least 3 characters. {{count}}/16',
												count: username.length,
											}),
											danger: true,
										}
									: isUsernameEdited
										? {
												text: t('T_SITE_PROFILE_USERNAME_HINT', {
													defaultValue: '3–16 characters. {{count}}/16',
													count: username.length,
												}),
												danger: false,
											}
										: { text: usernameRestHelper, danger: false }
					}
					onBuyUsernameChanger={buyUsernameChanger}
					descriptionLimit={DESCRIPTION_LIMIT}
					editable={!isSaving}
					isSaving={!!isSaving}
					isSaveInert={isSaveInert}
					isCancelLive={isDirty}
					submitError={submitError}
					onBack={onBack}
					onClose={onClose}
					onSave={save}
					onChangePhoto={changePhoto}
					avatarChangesNote={avatarChangesNote}
					isChangePhotoDisabled={isAvatarLocked}
					onContentHeight={updateDesktopHeight}
				/>
			</RedesignModal>
		);
	}

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			renderInline={renderInline}
			panel={{
				height: PANEL.height + lockedExtra,
				top: PANEL.top,
				overScreen: true,
				scrimColor: PROFILE_SCRIM,
			}}
			keyboardSubmitRef={saveRef}
		>
			{/* This sits below the controls, so tapping unused panel space only dismisses the keyboard. */}
			<Pressable
				accessibilityLabel={t('T_SITE_A11Y_DISMISS_KEYBOARD', { defaultValue: 'Dismiss keyboard' })}
				onPress={Keyboard.dismiss}
				style={StyleSheet.absoluteFill}
			/>
			<View style={styles.header}>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_BACK_TO_PROFILE', { defaultValue: 'Back to profile' })}
					onPress={onBack}
					style={styles.headerButton}
				>
					<Svg width={16 * scale} height={16 * scale} viewBox='0 0 16 16'>
						<Path
							d='M9.8 2.5 4.3 8l5.5 5.5'
							fill='none'
							stroke='#FFF5B9'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</Svg>
				</Pressable>
				<Text style={styles.title}>
					{t('T_SITE_PROFILE_EDIT', { defaultValue: 'Edit Profile' })}
				</Text>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CLOSE_EDIT_PROFILE', {
						defaultValue: 'Close edit profile',
					})}
					onPress={onClose}
					style={styles.closeButton}
				>
					<IconCloseSvg width={18 * scale} height={18 * scale} />
				</Pressable>
			</View>

			<View style={styles.avatarWrap}>
				{user?.isVip ? (
					<VipCrown width={42 * scale} height={37 * scale} style={styles.crown} />
				) : null}
				<View style={styles.avatarRing}>
					<Image source={avatarSource} resizeMode='cover' style={styles.avatar} />
				</View>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CHANGE_PROFILE_PHOTO', {
						defaultValue: 'Change profile photo',
					})}
					onPress={changePhoto}
					disabled={isSaving || isAvatarLocked}
					style={[styles.cameraButton, isAvatarLocked && styles.cameraLocked]}
				>
					<Image source={PictureIcon} resizeMode='contain' style={styles.cameraIcon} />
				</Pressable>
			</View>
			{avatarChangesNote ? (
				<Text numberOfLines={1} style={styles.avatarNote}>
					{avatarChangesNote}
				</Text>
			) : null}

			<View style={[styles.field, styles.usernameField]}>
				{fieldLabel(
					'user',
					t('T_SITE_PROFILE_MODAL_USERNAME', { defaultValue: 'Username' }),
					isUsernameLocked,
				)}
				{isUsernameLocked ? (
					<>
						<View style={[styles.input, styles.lockedInput]}>
							<Text numberOfLines={1} style={styles.lockedValue}>
								{user?.username}
							</Text>
							<LockGlyph size={14 * scale} />
						</View>
						<Text style={styles.helper}>{usernameLockedHelper}</Text>
						<View style={styles.buyChanger}>
							<BuyUsernameChangerButton
								onPress={buyUsernameChanger}
								height={36 * scale}
								radius={10 * scale}
								rim={Math.max(1, scale)}
								glow={9 * scale}
								fontSize={18 * scale}
								letterSpacing={0.36 * scale}
							/>
						</View>
					</>
				) : (
					<>
						<TextInput
							value={username}
							onChangeText={setUsername}
							editable={!isSaving}
							autoCapitalize='none'
							autoCorrect={false}
							blurOnSubmit
							maxLength={16}
							onSubmitEditing={Keyboard.dismiss}
							returnKeyType='done'
							style={styles.input}
							placeholder={t('T_SITE_PROFILE_MODAL_USERNAME', { defaultValue: 'Username' })}
							placeholderTextColor='#A88E86'
						/>
						<Text
							style={[
								styles.helper,
								(isUsernameTooShort || username.length === 16) && styles.limitHelper,
							]}
						>
							{isUsernameTooShort
								? t('T_SITE_PROFILE_USERNAME_TOO_SHORT', {
										defaultValue: 'Username must be at least 3 characters. {{count}}/16',
										count: username.length,
									})
								: isUsernameEdited
									? t('T_SITE_PROFILE_USERNAME_HINT', {
											defaultValue: '3–16 characters. {{count}}/16',
											count: username.length,
										})
									: usernameRestHelper}
						</Text>
					</>
				)}
			</View>

			<View style={styles.shortFields}>
				<View style={[styles.field, styles.ageField]}>
					{fieldLabel('age', t('T_SITE_PROFILE_MODAL_AGE', { defaultValue: 'Age' }))}
					<TextInput
						value={age}
						onChangeText={value => setAge(value.replace(/[^0-9]/g, '').slice(0, 2))}
						editable={!isSaving}
						keyboardType='number-pad'
						blurOnSubmit
						onSubmitEditing={Keyboard.dismiss}
						returnKeyType='done'
						style={[styles.input, !age && styles.emptyInput]}
						placeholder={t('T_SITE_PROFILE_MODAL_AGE', { defaultValue: 'Age' })}
						placeholderTextColor='#A88E86'
					/>
				</View>
				<View style={[styles.field, styles.locationField]}>
					{fieldLabel('location', t('T_SITE_PROFILE_MODAL_LOCATION', { defaultValue: 'Location' }))}
					<TextInput
						value={location}
						onChangeText={value => setLocation(value.slice(0, 30))}
						editable={!isSaving}
						maxLength={30}
						blurOnSubmit
						onSubmitEditing={Keyboard.dismiss}
						returnKeyType='done'
						style={[styles.input, !location && styles.emptyInput]}
						placeholder={t('T_SITE_PROFILE_LOCATION_PLACEHOLDER', {
							defaultValue: 'Add your location',
						})}
						placeholderTextColor='#A88E86'
					/>
				</View>
			</View>

			<View style={[styles.field, styles.descriptionField]}>
				<View style={styles.descriptionHeading}>
					{fieldLabel(
						'description',
						t('T_SITE_PROFILE_DESCRIPTION', { defaultValue: 'Description' }),
					)}
					<Text style={styles.counter}>
						{description.length}/{DESCRIPTION_LIMIT}
					</Text>
				</View>
				<TextInput
					value={description}
					onChangeText={setDescription}
					editable={!isSaving}
					maxLength={DESCRIPTION_LIMIT}
					multiline
					numberOfLines={2}
					scrollEnabled={false}
					style={[styles.input, styles.descriptionInput, !description && styles.emptyInput]}
					placeholder={t('T_SITE_PROFILE_DESCRIPTION_PLACEHOLDER', {
						defaultValue: 'Say something about yourself',
					})}
					placeholderTextColor='#A88E86'
					textAlignVertical='top'
				/>
			</View>

			{submitError ? <Text style={styles.error}>{submitError}</Text> : null}
			<View style={styles.actions}>
				<Pressable
					accessibilityRole='button'
					onPress={onBack}
					disabled={isSaving}
					style={[styles.action, styles.cancelAction, isSaving && styles.actionDisabled]}
				>
					<Text style={styles.cancelLabel}>
						{t('T_SITE_MODAL_NEW_ROOM_CANCEL', { defaultValue: 'Cancel' })}
					</Text>
				</Pressable>
				<Pressable
					ref={saveRef}
					accessibilityRole='button'
					onPress={save}
					disabled={isSaving || isSaveInert}
					style={[
						styles.action,
						styles.saveAction,
						isSaveInert && styles.saveInert,
						isSaving && styles.actionDisabled,
					]}
				>
					{!isSaveInert ? (
						<View pointerEvents='none' style={[StyleSheet.absoluteFill, styles.saveFill]}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id='editProfileSaveFill' x1='0' y1='0' x2='0' y2='1'>
										<Stop offset='0' stopColor='#FFF5B9' />
										<Stop offset='0.35' stopColor='#FFEE82' />
										<Stop offset='0.78' stopColor='#F5A300' />
										<Stop offset='1' stopColor='#CE7C00' />
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill='url(#editProfileSaveFill)' />
							</Svg>
						</View>
					) : null}
					{isSaving && !isSaveInert ? (
						<ActivityIndicator color='#2A0A04' size='small' />
					) : (
						<Text style={[styles.saveLabel, isSaveInert && styles.saveInertLabel]}>
							{t('T_SITE_PROFILE_MODAL_SAVE', { defaultValue: 'Save' })}
						</Text>
					)}
				</Pressable>
			</View>
		</RedesignModal>
	);
};

// `extra` is the Locked username field's added height; everything below that field moves down by it.
const createStyles = (scale: number, extra: number) =>
	StyleSheet.create({
		header: {
			position: 'absolute',
			top: 15 * scale,
			left: 11 * scale,
			width: 336 * scale,
			height: 32 * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		headerButton: {
			width: 32 * scale,
			height: 32 * scale,
			borderRadius: 16 * scale,
			backgroundColor: '#1A0603',
			borderWidth: Math.max(1, scale),
			borderColor: '#DA600E',
			alignItems: 'center',
			justifyContent: 'center',
		},
		closeButton: {
			width: 32 * scale,
			height: 32 * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		title: {
			color: '#FFF5B9',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 24 * scale,
			fontWeight: '700',
			lineHeight: 29 * scale,
			includeFontPadding: false,
		},
		// 12 above the artboard's 95, to fit the avatar-changes note between it and the username.
		avatarWrap: {
			position: 'absolute',
			top: 83 * scale,
			left: 115.5 * scale,
			width: 127 * scale,
			height: 127 * scale,
		},
		crown: { position: 'absolute', top: -20 * scale, left: 42.5 * scale, zIndex: 2 },
		avatarRing: {
			width: 127 * scale,
			height: 127 * scale,
			borderRadius: 63.5 * scale,
			borderWidth: 4 * scale,
			borderColor: '#FFE46D',
			backgroundColor: '#190301',
			overflow: 'hidden',
			...goldGlow,
		},
		avatar: { width: '100%', height: '100%' },
		cameraButton: {
			position: 'absolute',
			left: 88 * scale,
			top: 79 * scale,
			width: 54 * scale,
			height: 54 * scale,
			borderRadius: 27 * scale,
			backgroundColor: '#4C1007',
			borderWidth: Math.max(1, scale),
			borderColor: '#FFF908',
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF7200',
			shadowOpacity: 0.6,
			shadowRadius: 8 * scale,
			shadowOffset: { width: 0, height: 0 },
			elevation: 6,
		},
		cameraIcon: { width: 24 * scale, height: 24 * scale },
		cameraLocked: { opacity: 0.45 },
		avatarNote: {
			position: 'absolute',
			top: 220 * scale,
			left: 11 * scale,
			width: 336 * scale,
			color: '#A88E86',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			lineHeight: 15 * scale,
			textAlign: 'center',
		},
		field: {
			position: 'absolute',
			backgroundColor: '#330E07',
			borderWidth: Math.max(1, scale),
			borderColor: '#DA600E',
			borderRadius: 12 * scale,
		},
		usernameField: {
			left: 11 * scale,
			top: 244 * scale,
			width: 336 * scale,
			height: (107 + extra) * scale,
		},
		shortFields: {
			position: 'absolute',
			left: 11 * scale,
			top: (364 + extra) * scale,
			flexDirection: 'row',
			columnGap: 8 * scale,
		},
		ageField: { position: 'relative', width: 95 * scale, height: 85 * scale },
		locationField: { position: 'relative', width: 233 * scale, height: 85 * scale },
		label: {
			color: '#D9C4BC',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 14.4 * scale,
			includeFontPadding: false,
		},
		labelRow: {
			position: 'absolute',
			left: 12 * scale,
			top: 12 * scale,
			flexDirection: 'row',
			alignItems: 'center',
		},
		mutedLabel: { color: '#A88E86' },
		labelIcon: { width: 14 * scale, height: 14 * scale, marginRight: 8 * scale },
		vectorLabelIcon: { marginRight: 8 * scale },
		input: {
			position: 'absolute',
			left: 12 * scale,
			right: 12 * scale,
			top: 34 * scale,
			height: 38 * scale,
			paddingHorizontal: 10 * scale,
			paddingVertical: 0,
			borderWidth: Math.max(1, scale),
			borderColor: '#5C2416',
			borderRadius: 6 * scale,
			backgroundColor: '#330E07',
			color: '#FFF5B9',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '500',
		},
		emptyInput: { backgroundColor: '#0F0301' },
		// Locked: the value stays readable on the sunken fill, lock glyph right-aligned.
		lockedInput: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: 2 * scale,
			backgroundColor: '#0F0301',
		},
		lockedValue: {
			flex: 1,
			color: '#D9C4BC',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '500',
		},
		buyChanger: { position: 'absolute', left: 12 * scale, right: 12 * scale, top: 97 * scale },
		helper: {
			position: 'absolute',
			left: 12 * scale,
			top: 80 * scale,
			color: '#A88E86',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			lineHeight: 15 * scale,
		},
		limitHelper: { color: '#FF8B77' },
		descriptionField: {
			left: 11 * scale,
			top: (461 + extra) * scale,
			width: 336 * scale,
			height: 96 * scale,
		},
		descriptionHeading: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: 0,
			height: 34 * scale,
		},
		counter: {
			position: 'absolute',
			right: 12 * scale,
			top: 12 * scale,
			color: '#A88E86',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			lineHeight: 15 * scale,
		},
		descriptionInput: {
			top: 34 * scale,
			height: DESCRIPTION_HEIGHT * scale,
			paddingVertical: 6 * scale,
		},
		error: {
			position: 'absolute',
			left: 11 * scale,
			top: (550 + extra) * scale,
			width: 336 * scale,
			color: '#FF8B77',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			textAlign: 'center',
		},
		actions: {
			position: 'absolute',
			left: 11 * scale,
			top: (568 + extra) * scale,
			flexDirection: 'row',
			columnGap: 10 * scale,
		},
		action: {
			width: 163 * scale,
			height: 47 * scale,
			borderRadius: 10 * scale,
			borderWidth: Math.max(1, scale),
			alignItems: 'center',
			justifyContent: 'center',
		},
		cancelAction: { backgroundColor: '#330E07', borderColor: '#DA600E' },
		// No overflow clip: on iOS it would hide the glow, and saveFill rounds the gradient itself.
		saveAction: {
			borderColor: '#FFE45C',
			...goldGlow,
		},
		// Nothing to save: the button greys out and loses its glow.
		saveInert: {
			backgroundColor: '#330E07',
			borderColor: '#5C2416',
			shadowOpacity: 0,
			elevation: 0,
		},
		saveFill: { borderRadius: 9 * scale, overflow: 'hidden' },
		actionDisabled: { opacity: 0.55 },
		cancelLabel: {
			color: '#D9C4BC',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 18 * scale,
			fontWeight: '900',
			letterSpacing: 0.36 * scale,
			textTransform: 'uppercase',
		},
		saveLabel: {
			color: '#2A0A04',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 18 * scale,
			fontWeight: '900',
			letterSpacing: 0.36 * scale,
			textTransform: 'uppercase',
		},
		saveInertLabel: { color: '#A88E86' },
	});

export default ProfileRedesignEditView;
